import React, { useState } from "react";
import { useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { ApplicationScreen, Clinic, Doctor } from "../../models";
import { apiAccessRequest } from "../../constants";
import CircularProgress from "@mui/material/CircularProgress";
import { BuildUrl } from "../../helpers";
import Dialog from "@mui/material/Dialog";
import { useCreateAuditLog } from "../../hooks";

interface Props {
	open: boolean;
	doctor: Doctor | undefined;
	clinic: Clinic | undefined;
	close: (cancelled: boolean) => void;
}
export const ConfirmLogoutModal = React.memo(
	({ open, doctor, clinic, close }: Props) => {
		const { account } = useUserContext();
		const { instance } = useMsal();
		const [isError, setIsError] = useState(false);
		const [isLoading, setIsLoading] = useState(false);
		const { createAuditLog } = useCreateAuditLog();

		const handleConfirmLogoutClick = async () => {
			setIsError(false);
			setIsLoading(true);
			try {
				const requestUrl = BuildUrl(`doctor/${doctor?.id}/logout`);

				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					Authorization: `Bearer ${tokenResponse.accessToken}`,
					"Content-Type": "application/json"
				};

				const payload = { available: false, takingPatients: false };

				const response = await fetch(requestUrl, {
					method: "POST",
					headers,
					body: JSON.stringify(payload)
				});

				if (!response.ok) {
					setIsError(true);
					return;
				}
				reset(true);
				callCreateAuditLog();
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				setIsError(true);
			} finally {
				setIsLoading(false);
			}
		};

		const reset = (cancelled: boolean) => {
			setIsError(false);
			close(cancelled);
		};

		const callCreateAuditLog = async () => {
			try {
				await createAuditLog([
					{
						doctorId: doctor?.id,
						doctorName: doctor?.fullName,
						clinicId: clinic?.id,
						username: account?.name ?? "",
						applicationScreen: ApplicationScreen.AdminCentreOperations,
						action: "Logged out doctor"
					}
				]);
			} catch (error) {
				console.log("Error creating audit log", error);
			}
		};

		return (
			<Dialog maxWidth={false} open={open}>
				<div
					className="flex flex-col items-start p-5"
					style={{ width: 640, borderRadius: 24 }}
				>
					{!isError && (
						<>
							<div className="flex justify-between w-full pb-6">
								<div>
									{`Logout ${doctor?.salutation} ${doctor?.displayName}`}
								</div>
								<img
									src="images/svg/close.svg"
									width="20"
									height="20"
									alt="close modal"
									style={{ cursor: "pointer" }}
									onClick={() => reset(false)}
								/>
							</div>
							<div className="flex w-full py-6">
								{`Are you sure you want to logout ${doctor?.salutation} ${doctor?.displayName}.`}
							</div>

							<div className="flex justify-end w-full mt-6 gap-4">
								{!isLoading && (
									<>
										<button
											className="ch-button ch-button-primary justify-center"
											onClick={() => reset(false)}
										>
											Cancel
										</button>
										<button
											className="ch-button ch-button-secondary justify-center"
											onClick={() => handleConfirmLogoutClick()}
										>
											Yes, Logout
										</button>
									</>
								)}
								{isLoading && (
									<div className="flex justify-center py-3">
										<CircularProgress size="80px" />
									</div>
								)}
							</div>
						</>
					)}
					{isError && (
						<div className="flex flex-col items-center py-3">
							<img
								src="/images/svg/road-sign-warning.svg"
								height="50"
								alt="error icon"
							/>
							<p className="ch-m-text py-3 text-center">
								An error has occurred. The doctor's has not been logged out. Try
								again later
							</p>
							<div className="flex gap-3">
								<button
									className="ch-button ch-button-primary"
									onClick={() => reset(false)}
								>
									Go back
								</button>
							</div>
						</div>
					)}
				</div>
			</Dialog>
		);
	}
);
